/**
 * Wave 351-B · Inbox schemas — daily action queue items.
 *
 * Wave 351-A defined the contract; Wave 351-B widens it to what the
 * production GET /v1/inbox endpoint actually emits and what
 * `apps/web/src/pages/InboxPage` will consume in 351-C.
 *
 * Required surface (per the wave directive):
 *   - id, kind, severity, title, reason
 *   - primaryAction
 *   - evidence[]
 *   - source { table, id }
 *   - createdAt and/or dueAt
 *   - projectId (nullable when not project-scoped)
 *   - housyExplanation
 *   - dedupeKey (stable across reloads so the UI can de-dup)
 *   - prumoCardId (nullable; only set for proposal-derived items)
 */

import { z } from 'zod';
import { isoDateTimeSchema } from './common.js';

/**
 * Wave 351-B fix · brain-style relaxed id schema. The strict
 * common.ts `idSchema` regex (`^[a-z0-9]{24,32}$`) rejects both
 * UUIDs (with hyphens) and kebab-case capability ids that production
 * uses. Inbox item ids are namespaced strings like
 * `inbox:proposal:pp_01k…` so they need to be plain strings.
 */
const inboxIdSchema = z.string().min(1).max(160);

// ═══════════════════════════════════════════════════════════════════════════════
// Inbox Item Kind
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Wave 351-B emits three kinds (`proposal_pending`, `client_error_open`,
 * `self_heal_failed`). The other entries remain in the enum so future
 * waves can add specific surfaces (RDO missing, FVS overdue, etc.)
 * without another schema break.
 */
export const InboxItemKindEnum = z.enum([
  // Wave 351-B sources
  'proposal_pending',
  'client_error_open',
  'self_heal_failed',
  // Wave 100 v2 · jp-corrective
  'anomaly_pending',
  // Reserved for future waves
  'po_pending',
  'rnc_unassigned',
  'fvs_overdue',
  'doc_expiring',
  'anomaly_high',
  'rdo_missing',
  'medicao_draft',
]);
export type InboxItemKind = z.infer<typeof InboxItemKindEnum>;

// ═══════════════════════════════════════════════════════════════════════════════
// Severity
// ═══════════════════════════════════════════════════════════════════════════════

export const InboxItemSeverityEnum = z.enum(['info', 'warn', 'high', 'block']);
export type InboxItemSeverity = z.infer<typeof InboxItemSeverityEnum>;

// ═══════════════════════════════════════════════════════════════════════════════
// Tab (URL-facing)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Wave 351-B · pt-BR tab ids match the URL query param. The response
 * keys (today/pending/decided) stay English so they're unambiguous in
 * the wire shape.
 */
export const InboxTabEnum = z.enum(['hoje', 'pendencias', 'decisoes']);
export type InboxTab = z.infer<typeof InboxTabEnum>;

// ═══════════════════════════════════════════════════════════════════════════════
// Evidence chip & source
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Each evidence chip is a label + value the UI renders on the card.
 * Keep both as simple strings so the renderer is trivial — no nested
 * object trees, no untyped JSON blobs in this slot.
 */
export const InboxEvidenceItemSchema = z.object({
  label: z.string(),
  value: z.string(),
});
export type InboxEvidenceItem = z.infer<typeof InboxEvidenceItemSchema>;

/**
 * Stable source identity. The pair (table, id) uniquely identifies the
 * underlying row; the route uses it to compute `dedupeKey`.
 */
export const InboxItemSourceSchema = z.object({
  table: z.string(),
  id: z.string(),
});
export type InboxItemSource = z.infer<typeof InboxItemSourceSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Primary action
// ═══════════════════════════════════════════════════════════════════════════════

export const InboxPrimaryActionSchema = z.object({
  label: z.string(),
  /** Static link target — e.g. /admin/prumo. */
  href: z.string().optional(),
  /** Symbolic action name for dynamic dispatch in the UI. */
  callKind: z.string().optional(),
});
export type InboxPrimaryAction = z.infer<typeof InboxPrimaryActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Inbox Item DTO
// ═══════════════════════════════════════════════════════════════════════════════

export const InboxItemDTOSchema = z.object({
  id: inboxIdSchema,
  kind: InboxItemKindEnum,
  severity: InboxItemSeverityEnum,
  title: z.string(),
  /**
   * UX-facing "why this matters" copy. The directive renames the
   * earlier `why` field to `reason`.
   */
  reason: z.string(),
  primaryAction: InboxPrimaryActionSchema,
  evidence: z.array(InboxEvidenceItemSchema),
  source: InboxItemSourceSchema,
  createdAt: isoDateTimeSchema.nullable(),
  dueAt: isoDateTimeSchema.nullable(),
  projectId: z.string().nullable(),
  /** Housy context for this item — surfaced in the inbox card hover. */
  housyExplanation: z.string(),
  /** Stable id the UI uses to dedupe across renders. */
  dedupeKey: z.string(),
  /** Set when the item maps to a Prumo card (i.e. pm_proposals row). */
  prumoCardId: z.string().nullable(),
});
export type InboxItemDTO = z.infer<typeof InboxItemDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Inbox Response DTO
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The three response buckets correspond 1:1 with the URL tabs:
 *   today   ↔ ?tab=hoje
 *   pending ↔ ?tab=pendencias
 *   decided ↔ ?tab=decisoes
 *
 * When `tab` is set the route still returns all three keys, but only
 * the requested bucket is populated. This keeps the contract uniform
 * for the UI and avoids the "shape changes per param" trap.
 */
export const InboxResponseDTOSchema = z.object({
  today: z.array(InboxItemDTOSchema),
  pending: z.array(InboxItemDTOSchema),
  decided: z.array(InboxItemDTOSchema),
  generatedAt: isoDateTimeSchema,
});
export type InboxResponseDTO = z.infer<typeof InboxResponseDTOSchema>;
